import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { useTranslation } from 'react-i18next';
import Rewind from '../assets/Rewind.svg';
import '../i18n';
import ZoomToggle from './ZoomToggle';
import LocationIcon from '../assets/icons/LocationIcon.svg';
import ZoomableDiv from './ZoomableDiv';
import LoadingSpinner from './LoadingSpinner.js';
import { aspectoEntre, categoriaAstro, toLongitud } from '../utils/aspectos';
import {
  AstroGlyph,
  getPlanetIcon,
  getSignIconByIndex,
} from '../utils/astroIcons';
import { getPlanetFill, GlyphBackdrop } from '../utils/astroFills';
import { ReactComponent as AddIcon } from '../assets/icons/AddIcon.svg';
import { coloresAspectos, colorDeAspecto } from '../utils/coloresAspectos';

// Glifos de Ascendente y Medio Cielo, los mismos que usa la lista de planetas.
const AscGlyph = getPlanetIcon('Ascendente');
const McGlyph = getPlanetIcon('MC');
function Home() {
  const { t, i18n } = useTranslation();
  const { userData } = useUser();
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fechaActual, setFechaActual] = useState(new Date());
  const opcionesFecha = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
  const opcionesHora = { hour: '2-digit', minute: '2-digit', hour12: i18n.language === 'en' };
  const [modoTiempo, setModoTiempo] = useState('día');
  const [isZoomEnabled, setIsZoomEnabled] = useState(false);
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [geoCoords, setGeoCoords] = useState(null);
  const [resultadoConCasas, setResultadoConCasas] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [matrizAspectosConAngulos, setMatrizAspectosConAngulos] = useState([]);
  const [matrizAspectosCasa, setMatrizAspectosCasa] = useState([]);
  const [activeButton, setActiveButton] = useState('planetas');
  const [cuspidesSeleccionadas, setCuspidesSeleccionadas] = useState([]);
  const scrollTextListRef = useRef(null);
  const planetasSectionRef = useRef(null);
  const casasSectionRef = useRef(null);
  const ascNombre = i18n.language === 'es' ? 'Ascendente' : 'Ascendant';
  const mcNombre = 'MC';
  const resetearFormulario = () => {
    setRotationDegrees((prevDegrees) => prevDegrees + 720);
    setFechaActual(new Date());
  };
  const requestGeolocation = () => {
    if (!navigator.geolocation) return;
    if (geoCoords) {
      setGeoCoords(null);
      setResultadoConCasas(null);
      setMatrizAspectosConAngulos([]);
      setMatrizAspectosCasa([]);
      setCuspidesSeleccionadas([]);
      localStorage.setItem('homeGeoEnabled', 'false');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        localStorage.setItem('homeGeoEnabled', 'true');
      },
      () => setGeoLoading(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };
  useEffect(() => {
    if (localStorage.getItem('homeGeoEnabled') !== 'true') return;
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeoCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => setGeoLoading(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }, []);
  const scrollToSection = (sectionRef, sectionName) => {
    if (sectionRef.current && scrollTextListRef.current) {
      const containerTop = scrollTextListRef.current.getBoundingClientRect().top;
      const sectionTop = sectionRef.current.getBoundingClientRect().top;
      const scrollAmount = sectionTop - containerTop + scrollTextListRef.current.scrollTop;
      scrollTextListRef.current.scrollTo({ top: scrollAmount, behavior: 'smooth' });
      setActiveButton(sectionName);
    }
  };
  useEffect(() => {
    if (!resultadoConCasas) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === planetasSectionRef.current) setActiveButton('planetas');
            if (entry.target === casasSectionRef.current) setActiveButton('casas');
          }
        });
      },
      { root: scrollTextListRef.current, rootMargin: '0px 0px -50% 0px', threshold: 0 },
    );
    if (planetasSectionRef.current) observer.observe(planetasSectionRef.current);
    if (casasSectionRef.current) observer.observe(casasSectionRef.current);
    return () => observer.disconnect();
  }, [resultadoConCasas]);
  const toggleZoom = () => {
    setIsZoomEnabled((prev) => !prev);
  };
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };
  const fechaFormateada = `${fechaActual.toLocaleDateString(i18n.language, opcionesFecha)}, ${fechaActual.toLocaleTimeString(i18n.language, opcionesHora)}`;
  const fechaCapitalizada = capitalizeFirstLetter(fechaFormateada);
  const [matrizAspectos, setMatrizAspectos] = useState([]);
  const [aspectoFiltrado, setAspectoFiltrado] = useState(null);
  const [planetasSeleccionados, setPlanetasSeleccionados] = useState([]);
  // Planeta bajo el mouse en la carta (nombre con espacios, ej. "Nodo Norte").
  // El hover replica el efecto de selección en la lista y la tabla de aspectos.
  const [planetaHover, setPlanetaHover] = useState(null);
  // Cúspide bajo el mouse en la carta (id, ej. "home-house-3").
  const [cuspideHover, setCuspideHover] = useState(null);
  // Signo bajo el mouse en la carta (índice 0-11). Vive acá (no en CartaZodiacal)
  // para que los tres hovers se gestionen juntos: un hover nuevo borra los otros.
  const [signoHover, setSignoHover] = useState(null);
  // --- Gestión unificada de hovers de la carta -------------------------------
  // Sólo puede haber UN elemento hovereado a la vez (planeta, cúspide o signo).
  // Cada hover nuevo limpia los demás, y cualquier click selector los limpia
  // todos. Esto evita el bug de hover "trabado" cuando el mouse sale del SVG
  // sin disparar el onMouseLeave del elemento puntual.
  const limpiarHovers = () => {
    setPlanetaHover(null);
    setCuspideHover(null);
    setSignoHover(null);
  };
  const hoverPlaneta = (nombre) => {
    setPlanetaHover(nombre);
    setCuspideHover(null);
    setSignoHover(null);
  };
  const hoverCuspide = (id) => {
    setCuspideHover(id);
    setPlanetaHover(null);
    setSignoHover(null);
  };
  const hoverSigno = (i) => {
    setSignoHover(i);
    setPlanetaHover(null);
    setCuspideHover(null);
  };
  const [showContainers, setShowContainers] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const container1Ref = useRef(null);
  const container2Ref = useRef(null);
  const container3Ref = useRef(null);
  const planetasLista = useMemo(() => {
    return i18n.language === 'es'
      ? [
          'Sol',
          'Luna',
          'Mercurio',
          'Venus',
          'Marte',
          'Júpiter',
          'Saturno',
          'Urano',
          'Neptuno',
          'Plutón',
          'Quirón',
          'Lilith',
          'Nodo Norte',
          'Nodo Sur',
        ]
      : [
          'Sun',
          'Moon',
          'Mercury',
          'Venus',
          'Mars',
          'Jupiter',
          'Saturn',
          'Uranus',
          'Neptune',
          'Pluto',
          'Chiron',
          'Lilith',
          'North Node',
          'South Node',
        ];
  }, [i18n.language]);
  const planetasListaConAngulos = useMemo(() => {
    if (i18n.language === 'es')
      return [
        'Sol',
        'Luna',
        ascNombre,
        'Mercurio',
        'Venus',
        'Marte',
        'Júpiter',
        'Saturno',
        'Urano',
        'Neptuno',
        'Plutón',
        'Quirón',
        'Lilith',
        'Nodo Norte',
        'Nodo Sur',
        mcNombre,
      ];
    return [
      'Sun',
      'Moon',
      ascNombre,
      'Mercury',
      'Venus',
      'Mars',
      'Jupiter',
      'Saturn',
      'Uranus',
      'Neptune',
      'Pluto',
      'Chiron',
      'Lilith',
      'North Node',
      'South Node',
      mcNombre,
    ];
  }, [i18n.language, ascNombre, mcNombre]);
  const manejarClickPlaneta = (id) => {
    limpiarHovers();
    setPlanetasSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
    setCuspidesSeleccionadas([]);
  };
  const manejarClickCuspide = (cuspideId) => {
    limpiarHovers();
    setCuspidesSeleccionadas((prev) =>
      prev.includes(cuspideId) ? prev.filter((c) => c !== cuspideId) : [...prev, cuspideId],
    );
    setPlanetasSeleccionados([]);
  };
  // Scroll automático a la lista: al SELECCIONAR (no al deseleccionar) un planeta
  // o casa desde la carta, la tabla o la lista, llevamos su fila a la vista. La
  // lista de planetas usa el nombre con espacios y la carta la forma con guiones,
  // así que normalizamos al buscar.
  const prevSeleccionRef = useRef({ planetas: [], cuspides: [] });
  useEffect(() => {
    const prev = prevSeleccionRef.current;
    const nuevoPlaneta = planetasSeleccionados.find((id) => !prev.planetas.includes(id));
    const nuevaCuspide = cuspidesSeleccionadas.find((id) => !prev.cuspides.includes(id));
    prevSeleccionRef.current = {
      planetas: planetasSeleccionados,
      cuspides: cuspidesSeleccionadas,
    };
    const idNuevo = nuevoPlaneta || nuevaCuspide;
    if (!idNuevo) return; // deselección o filtro por aspecto: no scrolleamos
    requestAnimationFrame(() => {
      // Casas: scrolleamos al inicio de la lista de casas (no a la cúspide exacta).
      if (nuevaCuspide) {
        const seccion = casasSectionRef.current;
        if (seccion && typeof seccion.scrollIntoView === 'function') {
          seccion.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
      const el = document.getElementById(`li-${idNuevo.replace(/\s+/g, '-')}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }, [planetasSeleccionados, cuspidesSeleccionadas]);
  const handleAspectoClick = (aspectoNombre) => {
    limpiarHovers();
    setAspectoFiltrado((prev) => (prev === aspectoNombre ? null : aspectoNombre));
    setPlanetasSeleccionados([]);
    setCuspidesSeleccionadas([]);
  };
  const listaActiva = resultadoConCasas ? planetasListaConAngulos : planetasLista;
  const matrizActiva = resultadoConCasas ? matrizAspectosConAngulos : matrizAspectos;
  // Selección efectiva = seleccionados + el planeta bajo el mouse. Así el hover
  // colorea/filtra igual que un click, sin tocar la selección persistente.
  const seleccionEfectiva =
    planetaHover && !planetasSeleccionados.includes(planetaHover)
      ? [...planetasSeleccionados, planetaHover]
      : planetasSeleccionados;
  const cuspidesEfectivas =
    cuspideHover && !cuspidesSeleccionadas.includes(cuspideHover)
      ? [...cuspidesSeleccionadas, cuspideHover]
      : cuspidesSeleccionadas;
  const matrizFiltrada = matrizActiva.map((fila, indexFila) =>
    fila.map((aspectoInfo, indexColumna) => {
      if (cuspidesSeleccionadas.length > 0) return aspectoInfo;
      if (seleccionEfectiva.length > 0) {
        if (
          seleccionEfectiva.includes(listaActiva[indexFila]) ||
          seleccionEfectiva.includes(listaActiva[indexColumna])
        )
          return aspectoInfo;
        return indexFila === indexColumna ? '.' : { simbolo: '', nombre: '' };
      } else if (aspectoFiltrado) {
        return aspectoInfo?.nombre === aspectoFiltrado
          ? aspectoInfo
          : aspectoInfo?.nombre
            ? { simbolo: '', nombre: '' }
            : '';
      } else {
        return aspectoInfo;
      }
    }),
  );
  const planetCategories = useMemo(() => {
    return {
      luminarias: ['Sol', 'Luna'],
      internos: ['Mercurio', 'Venus', 'Marte', 'Júpiter', 'Saturno'],
      externos: ['Urano', 'Neptuno', 'Plutón'],
      otros: ['Ascendente', 'MC', 'NodoNorte', 'Chiron'],
    };
  }, []);
  useEffect(() => {
    const calcularTransitos = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `https://appstral-backend-798861160394.us-central1.run.app/astros_hoy?fecha=${fechaActual.toISOString()}&lang=${i18n.language}`,
        );
        const data = response.data;
        setResultado(data);
        if (data && data.planetas) {
          const numPlanetas = planetasLista.length;
          const nuevaMatriz = Array(numPlanetas)
            .fill(null)
            .map(() => Array(numPlanetas).fill(''));
          for (let i = 0; i < numPlanetas; i++) {
            for (let j = i + 1; j < numPlanetas; j++) {
              const planeta1Nombre = planetasLista[i];
              const planeta2Nombre = planetasLista[j];
              if (data.planetas[planeta1Nombre] && data.planetas[planeta2Nombre]) {
                const key = aspectoEntre(
                  data.planetas[planeta1Nombre].longitud,
                  categoriaAstro(planeta1Nombre, ascNombre, mcNombre),
                  data.planetas[planeta2Nombre].longitud,
                  categoriaAstro(planeta2Nombre, ascNombre, mcNombre),
                  userData,
                );
                if (key) {
                  nuevaMatriz[i][j] = { nombre: key };
                  nuevaMatriz[j][i] = { nombre: key };
                }
              }
            }
          }
          setMatrizAspectos(nuevaMatriz);
          setShowTable(true);
          if (data && data.planetas && nuevaMatriz.length > 0) {
            setShowContainers(true);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Error al obtener y procesar posiciones:', error);
        setLoading(false);
      }
    };
    calcularTransitos();
  }, [fechaActual, i18n.language, planetasLista, planetCategories, userData]);
  useEffect(() => {
    if (!geoCoords) return;
    const fetchCasas = async () => {
      setGeoLoading(true);
      try {
        // El backend interpreta `fecha` como hora LOCAL del lugar, así que la
        // armamos con componentes locales (igual que la app). NO usar
        // toISOString(), que convierte a UTC y corre el Ascendente varias horas.
        const year = fechaActual.getFullYear();
        const month = String(fechaActual.getMonth() + 1).padStart(2, '0');
        const day = String(fechaActual.getDate()).padStart(2, '0');
        const hours = String(fechaActual.getHours()).padStart(2, '0');
        const minutes = String(fechaActual.getMinutes()).padStart(2, '0');
        const fechaHoraFormateada = `${year}-${month}-${day}T${hours}:${minutes}`;

        const response = await fetch(
          'https://appstral-backend-798861160394.us-central1.run.app/calcular_carta',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fecha: fechaHoraFormateada,
              lat: geoCoords.lat,
              lon: geoCoords.lon,
              sistema_casas: userData?.sistemaCasas || 'Placidus',
              lang: i18n.language,
            }),
          },
        );
        if (response.ok) {
          const data = await response.json();
          setResultadoConCasas(data);
          if (data?.planetas && data?.casas?.['1'] && data?.casas?.['10']) {
            const signosEnOrden =
              i18n.language === 'es'
                ? [
                    'Aries',
                    'Tauro',
                    'Géminis',
                    'Cáncer',
                    'Leo',
                    'Virgo',
                    'Libra',
                    'Escorpio',
                    'Sagitario',
                    'Capricornio',
                    'Acuario',
                    'Piscis',
                  ]
                : [
                    'Aries',
                    'Taurus',
                    'Gemini',
                    'Cancer',
                    'Leo',
                    'Virgo',
                    'Libra',
                    'Scorpio',
                    'Sagittarius',
                    'Capricorn',
                    'Aquarius',
                    'Pisces',
                  ];
            const toLong = (signo, grado, minutos) => {
              const idx = signosEnOrden.indexOf(signo);
              return idx * 30 + grado + minutos / 60;
            };
            const longAsc = toLong(
              data.casas['1'].signo,
              data.casas['1'].grado,
              data.casas['1'].minutos,
            );
            const longMC = toLong(
              data.casas['10'].signo,
              data.casas['10'].grado,
              data.casas['10'].minutos,
            );
            const lista =
              i18n.language === 'es'
                ? [
                    'Sol',
                    'Luna',
                    ascNombre,
                    'Mercurio',
                    'Venus',
                    'Marte',
                    'Júpiter',
                    'Saturno',
                    'Urano',
                    'Neptuno',
                    'Plutón',
                    'Quirón',
                    'Lilith',
                    'Nodo Norte',
                    'Nodo Sur',
                    mcNombre,
                  ]
                : [
                    'Sun',
                    'Moon',
                    ascNombre,
                    'Mercury',
                    'Venus',
                    'Mars',
                    'Jupiter',
                    'Saturn',
                    'Uranus',
                    'Neptune',
                    'Pluto',
                    'Chiron',
                    'Lilith',
                    'North Node',
                    'South Node',
                    mcNombre,
                  ];
            const n = lista.length;
            const nuevaMatriz = Array(n)
              .fill(null)
              .map(() => Array(n).fill(''));
            const getLong = (nombre) => {
              if (nombre === ascNombre) return longAsc;
              if (nombre === mcNombre) return longMC;
              const p = data.planetas[nombre];
              return p ? p.longitud : null;
            };
            const calcDif = (a, b) => {
              const d = Math.abs(a - b);
              return Math.min(d, 360 - d);
            };
            const isMajorAspect = (deg) => [0, 90, 120, 180].some((a) => Math.abs(deg - a) <= 20);
            const getCategoria = (nombre) => {
              const lum = ['Sol', 'Luna', 'Sun', 'Moon'];
              const int = [
                'Mercurio',
                'Venus',
                'Marte',
                'Júpiter',
                'Saturno',
                'Mercury',
                'Venus',
                'Mars',
                'Jupiter',
                'Saturn',
              ];
              const ext = ['Urano', 'Neptuno', 'Plutón', 'Uranus', 'Neptune', 'Pluto'];
              if (lum.includes(nombre)) return 'luminarias';
              if (int.includes(nombre)) return 'internos';
              if (ext.includes(nombre)) return 'externos';
              if (nombre === ascNombre || nombre === mcNombre) return 'angulos';
              return 'otros';
            };
            const defMayor = userData?.mayores || {
              luminarias: 8,
              internos: 7,
              externos: 6,
              angulos: 5,
              otros: 5,
            };
            const defMenor = userData?.menores || {
              luminarias: 2,
              internos: 1.5,
              externos: 1,
              angulos: 3,
              otros: 0.5,
            };
            const getOrb = (n1, n2, mayor) => {
              const c1 = getCategoria(n1);
              const c2 = getCategoria(n2);
              const tbl = mayor ? defMayor : defMenor;
              const def = mayor ? 5 : 3;
              if (c1 === 'angulos') return tbl[c1] ?? def;
              if (c2 === 'angulos') return tbl[c2] ?? def;
              return Math.max(tbl[c1] ?? def, tbl[c2] ?? def);
            };
            const aspectoDef = [
              { deg: 0, key: 'conjuncion', es: 'conjunción', en: 'conjunction', mayor: true },
              { deg: 30, key: 'semisextil', es: 'semisextil', en: 'semi-sextile', mayor: false },
              { deg: 60, key: 'sextil', es: 'sextil', en: 'sextile', mayor: false },
              { deg: 90, key: 'cuadratura', es: 'cuadratura', en: 'square', mayor: true },
              { deg: 120, key: 'trigono', es: 'trígono', en: 'trine', mayor: true },
              { deg: 150, key: 'quincuncio', es: 'quincuncio', en: 'quincunx', mayor: false },
              { deg: 180, key: 'oposicion', es: 'oposición', en: 'opposition', mayor: true },
            ];
            for (let i = 0; i < n; i++) {
              for (let j = i + 1; j < n; j++) {
                const l1 = getLong(lista[i]);
                const l2 = getLong(lista[j]);
                if (l1 == null || l2 == null) continue;
                const dif = calcDif(l1, l2);
                const esMayor = isMajorAspect(dif);
                for (const asp of aspectoDef) {
                  const orb = getOrb(lista[i], lista[j], asp.mayor);
                  if (Math.abs(dif - asp.deg) <= orb) {
                    nuevaMatriz[i][j] = { nombre: asp.key };
                    nuevaMatriz[j][i] = { nombre: asp.key };
                    break;
                  }
                }
              }
            }
            setMatrizAspectosConAngulos(nuevaMatriz);
            const nuevaMatrizCasa = Array(n)
              .fill(null)
              .map(() => Array(12).fill(''));
            for (let i = 0; i < n; i++) {
              const l1 = getLong(lista[i]);
              if (l1 == null) continue;
              for (let j = 1; j <= 12; j++) {
                const casaData = data.casas[j.toString()];
                if (!casaData) continue;
                const l2 = toLong(casaData.signo, casaData.grado, casaData.minutos);
                const dif = calcDif(l1, l2);
                for (const asp of aspectoDef) {
                  const orb = getOrb(lista[i], 'Casa ' + j, asp.mayor);
                  if (Math.abs(dif - asp.deg) <= orb) {
                    nuevaMatrizCasa[i][j - 1] = { nombre: asp.key };
                    break;
                  }
                }
              }
            }
            setMatrizAspectosCasa(nuevaMatrizCasa);
          }
        }
      } catch (e) {
        console.error('Error al obtener casas:', e);
      } finally {
        setGeoLoading(false);
      }
    };
    fetchCasas();
  }, [geoCoords, fechaActual, i18n.language, userData?.sistemaCasas]);
  const ordenPlanetas =
    i18n.language === 'es'
      ? [
          'Sol',
          'Luna',
          'Mercurio',
          'Venus',
          'Marte',
          'Júpiter',
          'Saturno',
          'Urano',
          'Neptuno',
          'Plutón',
          'Quirón',
          'Lilith',
          'Nodo Norte',
          'Nodo Sur',
        ]
      : [
          'Sun',
          'Moon',
          'Mercury',
          'Venus',
          'Mars',
          'Jupiter',
          'Saturn',
          'Uranus',
          'Neptune',
          'Pluto',
          'Chiron',
          'Lilith',
          'North Node',
          'South Node',
        ];
const CartaZodiacal = ({
    planetasSeleccionados,
    manejarClickPlaneta,
    aspectoFiltrado,
    cartaConCasas,
    cuspidesSeleccionadas,
    manejarClickCuspide,
  }) => {
    const ascendente = cartaConCasas?.ascendente ?? 15;
    // signoHover / hoverSigno / limpiarHovers viven en Home (closure): así los
    // tres hovers de la carta se gestionan de forma unificada.
    const SVG_VIEWBOX_SIZE = 550;
    const center = SVG_VIEWBOX_SIZE / 2;
    const radio = center * 0.8;
    const radioInternoLineasAspectos = radio - 65;
    const [textLength, setTextLength] = useState(0);
    const signosNombres =
      i18n.language === 'es'
        ? [
            'Aries',
            'Tauro',
            'Géminis',
            'Cáncer',
            'Leo',
            'Virgo',
            'Libra',
            'Escorpio',
            'Sagitario',
            'Capricornio',
            'Acuario',
            'Piscis',
          ]
        : [
            'Aries',
            'Taurus',
            'Gemini',
            'Cancer',
            'Leo',
            'Virgo',
            'Libra',
            'Scorpio',
            'Sagittarius',
            'Capricorn',
            'Aquarius',
            'Pisces',
          ];
    const [animateChart, setAnimateChart] = useState(false);
    useEffect(() => {
      const hasAnimatedBefore = sessionStorage.getItem('hasChartAnimated');
      if (!hasAnimatedBefore) {
        setAnimateChart(true);
        sessionStorage.setItem('hasChartAnimated', 'true');
      }
    }, []);
    useEffect(() => {
      const handlePageUnload = () => {
        sessionStorage.removeItem('hasChartAnimated');
      };
      window.addEventListener('beforeunload', handlePageUnload);
      return () => {
        window.removeEventListener('beforeunload', handlePageUnload);
      };
    }, []);
    const calcularAngulo = (signo, grado) => {
      const signos =
        i18n.language === 'es'
          ? [
              'Aries',
              'Tauro',
              'Géminis',
              'Cáncer',
              'Leo',
              'Virgo',
              'Libra',
              'Escorpio',
              'Sagitario',
              'Capricornio',
              'Acuario',
              'Piscis',
            ]
          : [
              'Aries',
              'Taurus',
              'Gemini',
              'Cancer',
              'Leo',
              'Virgo',
              'Libra',
              'Scorpio',
              'Sagittarius',
              'Capricorn',
              'Aquarius',
              'Pisces',
            ];
      const indiceSigno = signos.indexOf(signo);
      const anguloTotal = indiceSigno * 30 + grado;
      return (-(anguloTotal - ascendente) + 180) * (Math.PI / 180);
    };
    const calcularAspecto = (planeta1, planeta2, userOrbes) => {
      const long1 =
        planeta1.longitud != null
          ? planeta1.longitud
          : toLongitud(planeta1.signo, planeta1.grado, planeta1.minutos);
      const long2 =
        planeta2.longitud != null
          ? planeta2.longitud
          : toLongitud(planeta2.signo, planeta2.grado, planeta2.minutos);
      // AC = Casa 1 y MC = Casa 10 cuentan como ángulos (igual que en la tabla).
      const cat = (nombre) =>
        nombre === 'Casa 1' || nombre === 'Casa 10'
          ? 'angulos'
          : categoriaAstro(nombre, ascNombre, mcNombre);
      return aspectoEntre(long1, cat(planeta1.nombre), long2, cat(planeta2.nombre), userOrbes);
    };
    // Misma fuente que la tabla con casas: planetas de calcular_carta si hay casas activas.
    const fuentePlanetas = cartaConCasas?.planetas || resultado.planetas;
    const planetas = Object.entries(fuentePlanetas);
    const aspectosEntrePlanetas = [];
    const planetasParaAspectos = [
      ...planetas.map(([nombre, data]) => ({ nombre, ...data, id: nombre.replace(/\s+/g, '-') })),
    ];
    if (cartaConCasas?.casas) {
      for (let i = 1; i <= 12; i++) {
        const casa = cartaConCasas.casas[i.toString()];
        if (casa) {
          planetasParaAspectos.push({
            nombre: `Casa ${i}`,
            signo: casa.signo,
            grado: casa.grado,
            minutos: casa.minutos,
            id: `home-house-${i}`,
          });
        }
      }
    }
    for (let i = 0; i < planetasParaAspectos.length; i++) {
      for (let j = i + 1; j < planetasParaAspectos.length; j++) {
        const planeta1 = planetasParaAspectos[i];
        const planeta2 = planetasParaAspectos[j];
        const isCusp1 = planeta1.nombre.startsWith('Casa ');
        const isCusp2 = planeta2.nombre.startsWith('Casa ');
        if (isCusp1 && isCusp2) continue;
        const aspecto = calcularAspecto(planeta1, planeta2, userData);
        if (aspecto) {
          aspectosEntrePlanetas.push({
            planeta1: planeta1.nombre,
            planeta2: planeta2.nombre,
            p1Id: planeta1.id,
            p2Id: planeta2.id,
            isCusp1,
            isCusp2,
            aspecto,
          });
        }
      }
    }
    const planetasResaltadosPorAspecto = aspectoFiltrado
      ? new Set(
          aspectosEntrePlanetas
            .filter((a) => a.aspecto === aspectoFiltrado)
            .filter((a) => {
              const inter = (id) =>
                typeof id === 'string' &&
                id.startsWith('home-house-') &&
                id !== 'home-house-1' &&
                id !== 'home-house-10';
              return !inter(a.p1Id) && !inter(a.p2Id);
            })
            .flatMap((a) => [a.p1Id, a.p2Id]),
        )
      : null;
    const colorAspectoActivo = aspectoFiltrado ? coloresAspectos[aspectoFiltrado] : null;
    const unificarId = (id) =>
      id === ascNombre ? 'home-house-1' : id === mcNombre ? 'home-house-10' : id;
    const esCuspideIntermedia = (id) =>
      typeof id === 'string' &&
      id.startsWith('home-house-') &&
      id !== 'home-house-1' &&
      id !== 'home-house-10';
    const planetasConAngulo = planetas.map(([nombre, planeta]) => {
      const angulo = calcularAngulo(planeta.signo, planeta.grado + planeta.minutos / 60);
      return { nombre, ...planeta, angulo };
    });
    planetasConAngulo.sort((a, b) => a.angulo - b.angulo);
    const grupos = [];
    let grupoActual = [];
    for (let i = 0; i < planetasConAngulo.length; i++) {
      const planetaActual = planetasConAngulo[i];
      const planetaSiguiente = planetasConAngulo[i + 1];
      grupoActual.push(planetaActual);
      let diferenciaMinima = Infinity;
      if (planetaSiguiente) {
        const diferenciaAngular = Math.abs(planetaActual.angulo - planetaSiguiente.angulo);
        diferenciaMinima = Math.min(diferenciaAngular, 2 * Math.PI - diferenciaAngular);
      }
      if (!planetaSiguiente || diferenciaMinima > (5 * Math.PI) / 180) {
        grupos.push(grupoActual);
        grupoActual = [];
      }
    }
    if (grupos.length > 1) {
      const primerPlaneta = grupos[0][0];
      const ultimoPlaneta = grupos[grupos.length - 1][grupos[grupos.length - 1].length - 1];
      const diferenciaAngular = Math.abs(ultimoPlaneta.angulo - primerPlaneta.angulo);
      const diferenciaMinima = Math.min(diferenciaAngular, 2 * Math.PI - diferenciaAngular);
      if (diferenciaMinima <= (5 * Math.PI) / 180) {
        grupos[0] = [...grupos[grupos.length - 1], ...grupos[0]];
        grupos.pop();
      }
    }
    // Tooltip de grado (grado° minutos' + glifo del signo) reutilizable por
    // planetas, AC y MC. Ancho dinámico según el contenido.
    const TooltipGrado = ({ cx, cy, signo, grado, minutos }) => {
      const signoIndex = signosNombres.indexOf(signo);
      const SignoTip = getSignIconByIndex(signoIndex);
      const label = `${grado}° ${minutos}'`;
      const tipH = 20;
      const glifoSize = 13;
      const padX = 8;
      const charW = 6.2; // ancho aprox por caracter en Effra @11px
      const textW = label.length * charW;
      const tipW = padX + textW + glifoSize + padX;
      const tipX = cx - tipW / 2;
      const tipY = cy - 34; // arriba del punto
      return (
        <g style={{ pointerEvents: 'none' }}>
          <rect x={tipX} y={tipY} width={tipW} height={tipH} rx={6} ry={6} fill="#7ebcec" />
          <text
            x={tipX + padX}
            y={tipY + tipH / 2}
            textAnchor="start"
            alignmentBaseline="middle"
            dominantBaseline="middle"
            fontSize={11}
            fill="var(--white)"
            fontFamily="Effra"
          >
            {label}
          </text>
          {SignoTip && (
            <SignoTip
              x={tipX + padX + textW}
              y={tipY + (tipH - glifoSize) / 2}
              width={glifoSize}
              height={glifoSize}
              fill="var(--white)"
            />
          )}
        </g>
      );
    };
    return (
      <svg
        id="carta-natal-svg"
        viewBox={`0 0 ${SVG_VIEWBOX_SIZE} ${SVG_VIEWBOX_SIZE}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', overflow: 'visible' }}
        className={animateChart ? 'chart-animation-initial' : ''}
        onMouseLeave={limpiarHovers}
      >
        {}
        {/* Anillo exterior dibujado como 12 arcos (uno por signo) con un corte
            angular en cada cúspide. El hueco es ausencia de trazo, no una línea
            de fondo tapando el círculo, así que funciona sobre cualquier fondo y
            los cortes caen exactos en cada grado 0/30. */}
        {signosNombres.map((_, i) => {
          const gap = 1; // grados de corte, centrado en la cúspide (0.5° a cada lado)
          const a1 = (-(i * 30 - ascendente) + 180 - gap / 2) * (Math.PI / 180);
          const a2 = (-((i + 1) * 30 - ascendente) + 180 + gap / 2) * (Math.PI / 180);
          const x1 = center + radio * Math.cos(a1);
          const y1 = center + radio * Math.sin(a1);
          const x2 = center + radio * Math.cos(a2);
          const y2 = center + radio * Math.sin(a2);
          // 30° < 180° → large-arc-flag = 0; el ángulo decrece → sweep-flag = 0
          return (
            <path
              key={i}
              d={`M ${x1} ${y1} A ${radio} ${radio} 0 0 0 ${x2} ${y2}`}
              fill="none"
              stroke={signoHover === i ? '#7ebcec' : 'var(--symbols)'}
              strokeWidth={signoHover === i ? 3 : 2}
              strokeLinecap="round"
              style={{ transition: 'stroke 0.15s ease, stroke-width 0.15s ease' }}
            />
          );
        })}
        {signosNombres.map((_, i) => {
          const baseAngle = i * 30;
          const adjustedAngle = (-(baseAngle - ascendente) + 180) * (Math.PI / 180);
          const rotatedAngle = adjustedAngle - (15 * Math.PI) / 180;
          const x = center + (radio + 25) * Math.cos(rotatedAngle);
          const y = center + (radio + 25) * Math.sin(rotatedAngle);
          const angleDeg = (rotatedAngle * 180) / Math.PI + 90;
          const SignoIcon = getSignIconByIndex(i);
          const signoSize = 30;
          const signoActivo = signoHover === i;
          // Cartel del nombre: lo corremos radialmente HACIA AFUERA del glifo
          // para que no quede tapado por el símbolo. Mismo tamaño/estilo que el
          // tooltip de planeta (celeste appstral, fontSize 11, ancho dinámico).
          const offsetCartel = 24;
          const cartelX = center + (radio + 25 + offsetCartel) * Math.cos(rotatedAngle);
          const cartelY = center + (radio + 25 + offsetCartel) * Math.sin(rotatedAngle);
          return (
            <g
              key={i}
              onMouseEnter={() => hoverSigno(i)}
              onMouseLeave={() => setSignoHover(null)}
              onClick={() => {
                // Toggle del signo; limpia sólo los OTROS hovers (no el propio,
                // que es el que estamos alternando).
                setPlanetaHover(null);
                setCuspideHover(null);
                setSignoHover((prev) => (prev === i ? null : i));
              }}
              style={{ cursor: 'pointer' }}
            >
              {/* Hit-area invisible: el glifo es un trazo fino, así que ampliamos
                  la zona clickeable/hovereable con un círculo transparente. */}
              <circle cx={x} cy={y} r={signoSize / 2 + 4} fill="transparent" />
              {SignoIcon && (
                <SignoIcon
                  x={x - signoSize / 2}
                  y={y - signoSize / 2}
                  width={signoSize}
                  height={signoSize}
                  fill={signoActivo ? '#7ebcec' : 'var(--text-color)'}
                  transform={`rotate(${angleDeg}, ${x}, ${y})`}
                  style={{ transition: 'fill 0.15s ease' }}
                />
              )}
              {signoActivo && (
                <g style={{ pointerEvents: 'none' }}>
                  <rect
                    x={cartelX - (textLength + 16) / 2}
                    y={cartelY - 10}
                    width={textLength + 16}
                    height={20}
                    fill="#7ebcec"
                    rx={6}
                    ry={6}
                  />
                  <text
                    x={cartelX}
                    y={cartelY}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={11}
                    fill="white"
                    fontFamily="Effra"
                    ref={(el) => {
                      if (el) {
                        setTextLength(el.getComputedTextLength());
                      }
                    }}
                  >
                    {signosNombres[i]}
                  </text>
                </g>
              )}
            </g>
          );
        })}


        {/* --- 1. CÍRCULO GRIS BASE (Fondo principal) --- */}
        <circle
          cx={center}
          cy={center}
          r={radioInternoLineasAspectos}
          fill="var(--grey-bg)"
          stroke="var(--text-color)"
          strokeWidth={1}
        />

        {/* --- 2. LÍNEAS DE CASAS (Van al fondo de todo) --- */}
        {cartaConCasas && (
          <>
            {[2, 3, 4, 5, 6].map((n) => {
              const dist = cartaConCasas[`distancia_ascendente_casa${n}`];
              const anguloRad = (-dist + 180) * (Math.PI / 180);
              const isCasa4 = n === 4;
              return (
                <line
                  key={`casa-linea-${n}`}
                  x1={center - radio * Math.cos(anguloRad)}
                  y1={center - radio * Math.sin(anguloRad)}
                  x2={center + radio * Math.cos(anguloRad)}
                  y2={center + radio * Math.sin(anguloRad)}
                  stroke="var(--symbols)"
                  strokeWidth={isCasa4 ? 1.5 : 0.5}
                  markerStart={isCasa4 ? 'url(#flecha10-home)' : undefined}
                />
              );
            })}
            <line
              x1={center + radio}
              y1={center}
              x2={center - radio}
              y2={center}
              stroke="var(--symbols)"
              strokeWidth={1.5}
              markerEnd="url(#flecha-home)"
            />
          </>
        )}

        {/* --- 3. CÍRCULO INTERNO RELLENO (Tapa el centro de las casas) --- */}
        {cartaConCasas && (
          <circle
            cx={center}
            cy={center}
            r={radioInternoLineasAspectos - 30}
            fill="var(--grey-bg)"
            stroke="var(--text-color)"
            strokeWidth={1}
          />
        )}

        {/* --- 4. LÍNEAS DE ASPECTO (Se dibujan por encima del círculo interno) --- */}
        {aspectosEntrePlanetas.map((aspecto, i) => {
          const { isCusp1, isCusp2 } = aspecto;
          const getData = (nombre, isCusp) => {
            if (isCusp) {
              const num = nombre.replace('Casa ', '');
              return cartaConCasas?.casas?.[num] ?? null;
            }
            return (cartaConCasas?.planetas || resultado.planetas)[nombre] ?? null;
          };
          const data1 = getData(aspecto.planeta1, isCusp1);
          const data2 = getData(aspecto.planeta2, isCusp2);
          if (!data1 || !data2) return null;
          // Usamos la selección EFECTIVA (incluye el hover de planeta/cúspide),
          // así pasar el mouse filtra las líneas igual que un click.
          const hasCuspSel = cuspidesEfectivas.length > 0;
          // La lista selecciona por nombre con espacios ("Nodo Norte"); los ids
          // de la rueda usan guiones ("Nodo-Norte"), así que normalizamos.
          const selIds = seleccionEfectiva.map((p) => unificarId(p).replace(/\s+/g, '-'));
          const cuspSelIds = cuspidesEfectivas.map(unificarId);
          const p1 = aspecto.p1Id;
          const p2 = aspecto.p2Id;
          let shouldShowAspect = false;
          if (selIds.length === 0 && !hasCuspSel) {
            // Por default mostramos planetas + AC/MC (como MyChart). Solo se
            // ocultan las cúspides intermedias (Casa 2-9, 11, 12).
            shouldShowAspect = !esCuspideIntermedia(p1) && !esCuspideIntermedia(p2);
          } else if (selIds.length > 0) {
            // Aspectos del planeta/ángulo seleccionado, sin casas intermedias
            // (para que coincida con la tabla, que solo tiene planetas + AC/MC).
            const involucra = selIds.includes(p1) || selIds.includes(p2);
            shouldShowAspect = involucra && !esCuspideIntermedia(p1) && !esCuspideIntermedia(p2);
          } else if (hasCuspSel) {
            shouldShowAspect = cuspSelIds.includes(p1) || cuspSelIds.includes(p2);
          }
          if (!shouldShowAspect) return null;
          if (aspectoFiltrado !== null && aspectoFiltrado !== aspecto.aspecto) return null;
          const p1Foco = selIds.includes(p1) || cuspSelIds.includes(p1);
          const p2Foco = selIds.includes(p2) || cuspSelIds.includes(p2);
          const esSolido = aspectoFiltrado ? true : (!isCusp1 || p1Foco) && (!isCusp2 || p2Foco);
          const angulo1 = calcularAngulo(data1.signo, data1.grado + data1.minutos / 60);
          const angulo2 = calcularAngulo(data2.signo, data2.grado + data2.minutos / 60);
          const colorAspecto = colorDeAspecto(aspecto.aspecto);
          const animationDelay = 2.0 + i * 0.05;
          const x1 = center + (radioInternoLineasAspectos - 0.5) * Math.cos(angulo1);
          const y1 = center + (radioInternoLineasAspectos - 0.5) * Math.sin(angulo1);
          const x2 = center + (radioInternoLineasAspectos - 0.5) * Math.cos(angulo2);
          const y2 = center + (radioInternoLineasAspectos - 0.5) * Math.sin(angulo2);
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={colorAspecto}
              strokeWidth={1}
              strokeDasharray={esSolido ? '0' : '3, 2'}
              opacity={esSolido ? 1 : 0.5}
              className={animateChart ? 'aspect-line-fade-in' : ''}
              style={{ animationDelay: `${animationDelay}s`, transition: 'all 0.3s ease' }}
            />
          );
        })}

        {/* --- 5. NÚMEROS DE CASAS, FLECHAS Y MC --- */}
        {cartaConCasas &&
          (() => {
            const casasLineas = [
              { nombre: 'Casa 2', distancia: cartaConCasas.distancia_ascendente_casa2 },
              { nombre: 'Casa 3', distancia: cartaConCasas.distancia_ascendente_casa3 },
              { nombre: 'Casa 4', distancia: cartaConCasas.distancia_ascendente_casa4 },
              { nombre: 'Casa 5', distancia: cartaConCasas.distancia_ascendente_casa5 },
              { nombre: 'Casa 6', distancia: cartaConCasas.distancia_ascendente_casa6 },
            ];
            const sectores = [
              { numero: 1, inicio: 0, fin: cartaConCasas.distancia_ascendente_casa2 },
              {
                numero: 2,
                inicio: cartaConCasas.distancia_ascendente_casa2,
                fin: cartaConCasas.distancia_ascendente_casa3,
              },
              {
                numero: 3,
                inicio: cartaConCasas.distancia_ascendente_casa3,
                fin: cartaConCasas.distancia_ascendente_casa4,
              },
              {
                numero: 4,
                inicio: cartaConCasas.distancia_ascendente_casa4,
                fin: cartaConCasas.distancia_ascendente_casa5,
              },
              {
                numero: 5,
                inicio: cartaConCasas.distancia_ascendente_casa5,
                fin: cartaConCasas.distancia_ascendente_casa6,
              },
              { numero: 6, inicio: cartaConCasas.distancia_ascendente_casa6, fin: 180 },
            ];
            return (
              <g>
                <defs>
                  <marker
                    id="flecha-home"
                    markerWidth="9"
                    markerHeight="9"
                    refX="7.5"
                    refY="4.5"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L6.3,4.5 L0,9" fill="none" stroke="var(--symbols)" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" />
                  </marker>
                  <marker
                    id="flecha10-home"
                    viewBox="0 0 20 20"
                    refX="16.5"
                    refY="10"
                    markerWidth="9"
                    markerHeight="9"
                    orient="auto-start-reverse"
                  >
                    <path d="M 0 0 L 14 10 L 0 20" fill="none" stroke="var(--symbols)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </marker>
                </defs>
                {casasLineas.map((casa, i) => {
                  const anguloRad = (-casa.distancia + 180) * (Math.PI / 180);
                  const isCasa4 = casa.nombre === 'Casa 4';
                  const radioCirculo = radio - 375;
                  const xMC = center + radioCirculo * Math.cos(anguloRad);
                  const yMC = center + radioCirculo * Math.sin(anguloRad);
                  if (!isCasa4) return null;
                  return (
                    <g key={i}>
                      {isCasa4 && (
                        <g
                          onClick={() => manejarClickPlaneta?.(mcNombre)}
                          onMouseEnter={() => hoverPlaneta(mcNombre)}
                          onMouseLeave={() => setPlanetaHover(null)}
                          style={{ cursor: 'pointer' }}
                        >
                          <circle
                            cx={xMC}
                            cy={yMC}
                            r={9.3}
                            cursor="pointer"
                            fill={
                              seleccionEfectiva.includes(mcNombre) ||
                              cuspidesEfectivas.includes('home-house-10')
                                ? '#7ebcec'
                                : 'var(--grey-bg)'
                            }
                            stroke={
                              seleccionEfectiva.includes(mcNombre) ||
                              cuspidesEfectivas.includes('home-house-10')
                                ? '#7ebcec'
                                : 'var(--symbols)'
                            }
                            strokeWidth={1}
                          />
                          <McGlyph
                            x={xMC - 6.65}
                            y={yMC - 6.65}
                            width={13.3}
                            height={13.3}
                            fill={
                              seleccionEfectiva.includes(mcNombre) ||
                              cuspidesEfectivas.includes('home-house-10')
                                ? '#ffffff'
                                : 'var(--symbols)'
                            }
                            style={{ pointerEvents: 'none' }}
                          />
                        </g>
                      )}
                    </g>
                  );
                })}
                
                {sectores.map((sector, i) => {
                  const anguloMedio = (sector.inicio + sector.fin) / 2;
                  const anguloRad = (-anguloMedio + 180) * (Math.PI / 180);
                  const radioNumero = radioInternoLineasAspectos - 15;
                  const x = center + radioNumero * Math.cos(anguloRad);
                  const y = center + radioNumero * Math.sin(anguloRad);
                  const xOp = center + radioNumero * Math.cos(anguloRad + Math.PI);
                  const yOp = center + radioNumero * Math.sin(anguloRad + Math.PI);
                  const id1 = `home-house-${sector.numero}`;
                  const id2 = `home-house-${sector.numero + 6}`;
                  const sel1 = cuspidesEfectivas.includes(id1);
                  const sel2 = cuspidesEfectivas.includes(id2);
                  return (
                    <g key={i}>
                      <text
                        x={x}
                        y={y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={18}
                        fontWeight={400}
                        fill={sel1 ? '#7ebcec' : 'var(--symbols)'}
                        fontFamily="Effra"
                        style={{ cursor: 'pointer' }}
                        onClick={() => manejarClickCuspide?.(id1)}
                        onMouseEnter={() => hoverCuspide(id1)}
                        onMouseLeave={() => setCuspideHover(null)}
                      >
                        {sector.numero}
                      </text>
                      <text
                        x={xOp}
                        y={yOp}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={18}
                        fontWeight={400}
                        fill={sel2 ? '#7ebcec' : 'var(--symbols)'}
                        fontFamily="Effra"
                        style={{ cursor: 'pointer' }}
                        onClick={() => manejarClickCuspide?.(id2)}
                        onMouseEnter={() => hoverCuspide(id2)}
                        onMouseLeave={() => setCuspideHover(null)}
                      >
                        {sector.numero + 6}
                      </text>
                    </g>
                  );
                })}
              </g>
            );
          })()}

        {grupos.map((grupo) => {
          const cantidad = grupo.length;
          // --- Separación de cuerpos en conjunción (portado de NatalChartView) ---
          // La separación es principalmente RADIAL (los símbolos se escalonan a
          // distinto radio manteniendo el ángulo); el abanico ANGULAR solo se
          // agrega cuando el escalonado radial por sí solo no alcanza la
          // distancia mínima entre símbolos. Además se sesga hacia afuera, donde
          // hay más lugar. Constantes tuneables.
          const DIST_MIN_SIMBOLOS = 18; // separación mín (u) entre símbolos contiguos
          const SEPARACION_RADIAL = 18; // paso radial ideal entre anillos
          const RANGO_RADIAL_MAX = 30; // ancho máx (u) de la banda radial por grupo
          const SEPARACION_ANGULAR_MIN = (4 * Math.PI) / 180; // abanico mínimo (rad)
          const SESGO_EXTERIOR = 0.5; // 0 = centrado; 1 = todo el escalonado hacia afuera
          const radioLineaBase = radio - 45;

          // Centro angular del grupo (desenrollando por si cruza el 0°).
          const base = grupo[0].angulo;
          const centroAng =
            grupo.reduce(
              (s, p) => s + (p.angulo < base ? p.angulo + 2 * Math.PI : p.angulo),
              0,
            ) / cantidad;

          // Paso radial: el ideal, recortado para que el grupo entero entre en la
          // banda. Cuantos más cuerpos, más chico el paso.
          const pasoRadial = Math.min(SEPARACION_RADIAL, RANGO_RADIAL_MAX / Math.max(cantidad - 1, 1));
          // Si el escalonado radial ya separa lo suficiente, no hace falta abanico;
          // si no, se calcula con Pitágoras el arco que falta para llegar al mínimo.
          const arco =
            pasoRadial >= DIST_MIN_SIMBOLOS
              ? 0
              : Math.sqrt(DIST_MIN_SIMBOLOS * DIST_MIN_SIMBOLOS - pasoRadial * pasoRadial);
          const pasoAngular = Math.max(SEPARACION_ANGULAR_MIN, arco / radioLineaBase);
          const desplazamientoExterior = SESGO_EXTERIOR * ((cantidad - 1) / 2) * pasoRadial;

          return grupo.map((planeta, index) => {
            let radioBase;
            let anguloFinal;
            if (cantidad === 1) {
              radioBase = radioLineaBase;
              anguloFinal = planeta.angulo;
            } else {
              const rel = index - (cantidad - 1) / 2; // índice centrado: ..., -1, 0, 1, ...
              radioBase = radioLineaBase + rel * pasoRadial + desplazamientoExterior;
              anguloFinal = centroAng + rel * pasoAngular;
            }
            const x = center + radioBase * Math.cos(anguloFinal);
            const y = center + radioBase * Math.sin(anguloFinal);
            const planetaId = `${planeta.nombre.replace(/\s+/g, '-')}`;
            const estaHover = planetaHover === planeta.nombre;
            const colorPlaneta =
              planetasSeleccionados.map((p) => p.replace(/\s+/g, '-')).includes(planetaId) ||
              estaHover
                ? '#7ebcec'
                : planetasResaltadosPorAspecto?.has(planetaId)
                  ? colorAspectoActivo
                  : 'var(--dark-grey)';
            const colorTick = colorPlaneta === '#7ebcec' ? '#7ebcec' : 'var(--symbols)';
            const offsetInterior = 0; // mismo aire que offsetExterior contra el anillo
            const longitudLinea = 5;
            // El tick marca el grado REAL del planeta en la rueda
            // (planeta.angulo), no el ángulo desplazado para legibilidad
            // (anguloFinal). Así sigue señalando la posición exacta aunque el
            // glifo se abanique angularmente en una conjunción.
            const anguloHaciaCentro = planeta.angulo + Math.PI;
            const lineaX1 =
              center + (radioInternoLineasAspectos + offsetInterior + longitudLinea) * Math.cos(planeta.angulo);
            const lineaY1 =
              center + (radioInternoLineasAspectos + offsetInterior + longitudLinea) * Math.sin(planeta.angulo);
            const lineaX2 = lineaX1 + longitudLinea * Math.cos(anguloHaciaCentro);
            const lineaY2 = lineaY1 + longitudLinea * Math.sin(anguloHaciaCentro);
            // Segundo tick: mismo grado real (planeta.angulo) pero pegado al
            // círculo EXTERIOR (anillo de signos, radio), apuntando hacia
            // adentro. Marca la posición exacta sobre el zodíaco.
            const longitudTickExterior = 6;
            const offsetExterior = 1; // cuánto se despega del círculo de signos hacia adentro
            const tickExtX1 = center + (radio - offsetExterior) * Math.cos(planeta.angulo);
            const tickExtY1 = center + (radio - offsetExterior) * Math.sin(planeta.angulo);
            const tickExtX2 =
              center + (radio - offsetExterior - longitudTickExterior) * Math.cos(planeta.angulo);
            const tickExtY2 =
              center + (radio - offsetExterior - longitudTickExterior) * Math.sin(planeta.angulo);
            return (
              <g
                key={planeta.nombre}
                id={planetaId}
                onClick={() => manejarClickPlaneta(planetaId)}
                onMouseEnter={() => hoverPlaneta(planeta.nombre)}
                onMouseLeave={() => setPlanetaHover(null)}
                style={{ cursor: 'pointer' }}
              >
                {}
                {/* Base invisible para ampliar el área clickeable del glifo */}
                <circle cx={x} cy={y} r={13} fill="transparent" />
                <line
                  x1={lineaX1}
                  y1={lineaY1}
                  x2={lineaX2}
                  y2={lineaY2}
                  stroke={colorTick}
                  strokeWidth="1"
                />
                <line
                  x1={tickExtX1}
                  y1={tickExtY1}
                  x2={tickExtX2}
                  y2={tickExtY2}
                  stroke={colorTick}
                  strokeWidth="1"
                />
                {(() => {
                  const PlanetaIcon = getPlanetIcon(planeta.nombre);
                  const PlanetaFill = getPlanetFill(planeta.nombre);
                  const planetaSize = 22;
                  return PlanetaIcon ? (
                    <>
                      <GlyphBackdrop fill={PlanetaFill} color="var(--grey-bg)" x={x - planetaSize / 2} y={y - planetaSize / 2} size={planetaSize} />
                      <PlanetaIcon
                        x={x - planetaSize / 2}
                        y={y - planetaSize / 2}
                        width={planetaSize}
                        height={planetaSize}
                        fill={colorPlaneta}
                      />
                    </>
                  ) : null;
                })()}
                {/* Estado del planeta: st (estacionario), Rx (retrógrado) o
                    stRx (ambos). Mismo criterio que la rueda de iOS. */}
                {(() => {
                  const sufijo =
                    planeta['retrógrado'] && planeta['estacionario']
                      ? 'stRx'
                      : planeta['retrógrado']
                        ? 'Rx'
                        : planeta['estacionario']
                          ? 'st'
                          : null;
                  return sufijo ? (
                    <text
                      x={x + 5}
                      y={y + 12}
                      fontSize={8}
                      fill={colorPlaneta}
                      fontFamily="Effra"
                    >
                      {sufijo}
                    </text>
                  ) : null;
                })()}
                {/* Tooltip al hacer hover: grado° minutos' + glifo del signo. */}
                {estaHover && (
                  <TooltipGrado
                    cx={x}
                    cy={y}
                    signo={planeta.signo}
                    grado={planeta.grado}
                    minutos={planeta.minutos}
                  />
                )}
              </g>
            );
          });
        })}
        {cartaConCasas && (
          <g
            onClick={() => manejarClickPlaneta(ascNombre)}
            onMouseEnter={() => hoverPlaneta(ascNombre)}
            onMouseLeave={() => setPlanetaHover(null)}
            style={{ cursor: 'pointer' }}
          >
            <circle
              cx={center - radio + 65}
              cy={center}
              r={9.3}
              cursor="pointer"
              fill={
                seleccionEfectiva.includes(ascNombre) || cuspidesEfectivas.includes('home-house-1')
                  ? '#7ebcec'
                  : 'var(--grey-bg)'
              }
              stroke={
                seleccionEfectiva.includes(ascNombre) || cuspidesEfectivas.includes('home-house-1')
                  ? '#7ebcec'
                  : 'var(--symbols)'
              }
              strokeWidth={1}
            />
            <AscGlyph
              x={center - radio + 65 - 6.65}
              y={center - 6.65}
              width={13.3}
              height={13.3}
              fill={
                seleccionEfectiva.includes(ascNombre) || cuspidesEfectivas.includes('home-house-1')
                  ? '#ffffff'
                  : 'var(--symbols)'
              }
              style={{ pointerEvents: 'none' }}
            />
          </g>
        )}

        {/* Tooltips de AC y MC al final del SVG: así quedan por encima de líneas
            y planetas y no los tapa nada. */}
        {cartaConCasas?.casas?.['1'] && planetaHover === ascNombre && (
          <TooltipGrado
            cx={center - radio + 65}
            cy={center}
            signo={cartaConCasas.casas['1'].signo}
            grado={cartaConCasas.casas['1'].grado}
            minutos={cartaConCasas.casas['1'].minutos}
          />
        )}
        {cartaConCasas?.casas?.['10'] &&
          planetaHover === mcNombre &&
          (() => {
            const distMC = cartaConCasas.distancia_ascendente_casa4;
            const anguloRadMC = (-distMC + 180) * (Math.PI / 180);
            const radioCirculoMC = radio - 375;
            const xMC = center + radioCirculoMC * Math.cos(anguloRadMC);
            const yMC = center + radioCirculoMC * Math.sin(anguloRadMC);
            return (
              <TooltipGrado
                cx={xMC}
                cy={yMC}
                signo={cartaConCasas.casas['10'].signo}
                grado={cartaConCasas.casas['10'].grado}
                minutos={cartaConCasas.casas['10'].minutos}
              />
            );
          })()}
      </svg>
    );
  };
  const sumarTiempo = () => {
    setFechaActual((prevFecha) => {
      const nuevaFecha = new Date(prevFecha);
      switch (modoTiempo) {
        case 'minuto':
          nuevaFecha.setMinutes(nuevaFecha.getMinutes() + 1);
          break;
        case 'hora':
          nuevaFecha.setHours(nuevaFecha.getHours() + 1);
          break;
        case 'día':
          nuevaFecha.setDate(nuevaFecha.getDate() + 1);
          break;
        case 'semana':
          nuevaFecha.setDate(nuevaFecha.getDate() + 7);
          break;
        case 'mes':
          nuevaFecha.setMonth(nuevaFecha.getMonth() + 1);
          break;
        case 'año':
          nuevaFecha.setFullYear(nuevaFecha.getFullYear() + 1);
          break;
        default:
          break;
      }
      return nuevaFecha;
    });
  };
  const restarTiempo = () => {
    setFechaActual((prevFecha) => {
      const nuevaFecha = new Date(prevFecha);
      switch (modoTiempo) {
        case 'minuto':
          nuevaFecha.setMinutes(nuevaFecha.getMinutes() - 1);
          break;
        case 'hora':
          nuevaFecha.setHours(nuevaFecha.getHours() - 1);
          break;
        case 'día':
          nuevaFecha.setDate(nuevaFecha.getDate() - 1);
          break;
        case 'semana':
          nuevaFecha.setDate(nuevaFecha.getDate() - 7);
          break;
        case 'mes':
          nuevaFecha.setMonth(nuevaFecha.getMonth() - 1);
          break;
        case 'año':
          nuevaFecha.setFullYear(nuevaFecha.getFullYear() - 1);
          break;
        default:
          break;
      }
      return nuevaFecha;
    });
  };
  const cambiarModoTiempo = () => {
    setModoTiempo((prevModo) => {
      const modos = ['minuto', 'hora', 'día', 'semana', 'mes', 'año'];
      const index = modos.indexOf(prevModo);
      return modos[(index + 1) % modos.length];
    });
  };
  const getRowAnimationDelay = (rowIndex) => {
    return `${rowIndex * 0.03}s`;
  };
  return (
    <>
      {(loading || geoLoading) && <LoadingSpinner />}
      <div className="home-container">
        {}
        <div ref={container1Ref} className="square-item">
          {}
          <div className="top-space">
            <p className="fecha-text">{fechaCapitalizada}</p>
          </div>
          <div className="wheel-frame">
          <ZoomToggle
            isZoomEnabled={isZoomEnabled}
            onToggle={toggleZoom}
          />
          <ZoomableDiv
            zoomFactor={2}
            magnifierSize={180}
            isZoomEnabled={isZoomEnabled}
            overflowVisibleWhenIdle
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: 'var(--grey-bg)',
                borderRadius: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxSizing: 'border-box',
                overflow: 'visible',
                position: 'relative',
                margin: 0,
              }}
            >
              {resultado && (
                <div
                  style={{
                    width: '100%',
                    overflow: 'visible',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    alignContent: 'center',
                  }}
                >
                  <CartaZodiacal
                    aspectoFiltrado={aspectoFiltrado}
                    planetasSeleccionados={planetasSeleccionados}
                    manejarClickPlaneta={manejarClickPlaneta}
                    userData={userData}
                    cartaConCasas={resultadoConCasas}
                    cuspidesSeleccionadas={cuspidesSeleccionadas}
                    manejarClickCuspide={manejarClickCuspide}
                  />
                </div>
              )}
            </div>
          </ZoomableDiv>
          </div>
          <div className="time-controls-container">
            <button onClick={restarTiempo} className="mi-boton">
              -
            </button>
            <button onClick={cambiarModoTiempo} className="mi-boton mi-boton-medio">
              {t(`${modoTiempo}`)}
            </button>
            <button onClick={sumarTiempo} className="mi-boton">
              +
            </button>
            <button onClick={resetearFormulario} className="mi-boton">
              <img
                src={Rewind}
                className="rewind-icon"
                style={{ transform: `rotate(${rotationDegrees}deg)` }}
                alt="Rewind"
              />
            </button>
            <button
              onClick={requestGeolocation}
              className="mi-boton"
              title={
                geoCoords
                  ? i18n.language === 'es'
                    ? 'Desactivar casas'
                    : 'Disable houses'
                  : i18n.language === 'es'
                    ? 'Usar mi ubicación'
                    : 'Use my location'
              }
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <img
                src={LocationIcon}
                alt="location"
                style={{
                  width: '.9em',
                  height: '.9em',
                  filter: geoCoords ? 'none' : 'grayscale(1) brightness(0.7)',
                  transition: 'filter 0.3s ease',
                }}
              />
            </button>
          </div>
        </div>
        {}
        <div ref={container2Ref} className="square-item">
          <div className="top-space" style={{ justifyContent: 'start' }}>
            {resultadoConCasas && (
              <>
                <button
                  className={`chartscroll-button ${activeButton === 'planetas' ? 'active-button' : ''}`}
                  onClick={() => scrollToSection(planetasSectionRef, 'planetas')}
                >
                  {i18n.language === 'es' ? 'Planetas' : 'Planets'}
                </button>
                <button
                  className={`chartscroll-button ${activeButton === 'casas' ? 'active-button' : ''}`}
                  onClick={() => scrollToSection(casasSectionRef, 'casas')}
                >
                  {i18n.language === 'es' ? 'Casas' : 'Houses'}
                </button>
              </>
            )}
          </div>
          <div className="square-item-content-wrapper">
            {(cuspidesSeleccionadas.length > 0 || planetasSeleccionados.length > 0) && (
              <button
                onClick={() => {
                  limpiarHovers();
                  setCuspidesSeleccionadas([]);
                  setPlanetasSeleccionados([]);
                }}
                aria-label={i18n.language === 'es' ? 'Deseleccionar todo' : 'Deselect all'}
                title={
                  i18n.language === 'es' 
                    ? 'Deseleccionar planetas y cúspides'
                    : 'Deselect planets and cusps'
                }
                className="deselect-btn"
              > 
                <AddIcon className="deselect-x" />
              </button>
            )}
            {resultado && resultado.planetas && (
              <ul
                ref={scrollTextListRef}
                className="planet-list"
                style={{ opacity: showContainers ? 1 : 0, gap: '1px' }}
              >
                <div ref={planetasSectionRef} style={{ width: '100%' }} />
                {(resultadoConCasas ? planetasListaConAngulos : ordenPlanetas).map(
                  (nombre, index) => {
                    const initialDelay = 0.1;
                    const totalDelay = initialDelay + index * 0.01;
                    let datos = null;
                    let texto = '';
                    if (nombre === ascNombre) {
                      const casa1 = resultadoConCasas?.casas?.['1'];
                      if (!casa1) return null;
                      datos = casa1;
                      texto =
                        i18n.language === 'es'
                          ? `${ascNombre} en ${casa1.grado}°${casa1.minutos}' de ${casa1.signo}`
                          : `${ascNombre} at ${casa1.grado}°${casa1.minutos}' in ${casa1.signo}`;
                    } else if (nombre === mcNombre) {
                      const casa10 = resultadoConCasas?.casas?.['10'];
                      if (!casa10) return null;
                      datos = casa10;
                      texto =
                        i18n.language === 'es'
                          ? `Medio Cielo en ${casa10.grado}°${casa10.minutos}' de ${casa10.signo}`
                          : `Midheaven at ${casa10.grado}°${casa10.minutos}' in ${casa10.signo}`;
                    } else {
                      datos = (resultadoConCasas?.planetas || resultado.planetas)[nombre];
                      if (!datos) return null;
                      texto =
                        i18n.language === 'es'
                          ? `${nombre} en ${datos.grado}°${datos.minutos}'${datos['estacionario'] ? ' st' : ''}${datos['retrógrado'] ? 'Rx' : ''} de ${datos.signo}`
                          : `${nombre} at ${datos.grado}°${datos.minutos}'${datos['estacionario'] ? ' st' : ''}${datos['retrógrado'] ? 'Rx' : ''} in ${datos.signo}`;
                    }
                    return (
                      <li
                        key={nombre}
                        id={`li-${nombre.replace(/\s+/g, '-')}`}
                        className={`planet-list-item ${seleccionEfectiva.includes(nombre) ? 'selected' : ''}`}
                        style={{ animation: `fadeIn 0.3s ease-out forwards ${totalDelay}s` }}
                        onClick={() => manejarClickPlaneta(nombre)}
                      >
                        <span className="planet-symbol">
                          <AstroGlyph kind="planet" name={nombre} />
                        </span>
                        {texto}
                      </li>
                    );
                  },
                )}
                {resultadoConCasas?.casas && (
                  <>
                    <li
                      ref={casasSectionRef}
                      style={{
                        width: '90%',
                        height: '1px',
                        backgroundColor: 'var(--grey-bg)',
                        margin: '4px auto',
                        listStyle: 'none',
                        alignSelf: 'center',
                      }}
                    />
                    {Object.keys(resultadoConCasas.casas).map((numeroCasa, index) => {
                      const casa = resultadoConCasas.casas[numeroCasa];
                      const cuspideId = `home-house-${numeroCasa}`;
                      const totalDelay = 0.1 + (planetasListaConAngulos.length + index) * 0.01;
                      return (
                        <li
                          key={cuspideId}
                          className="house-list-item"
                          style={{
                            opacity: 0,
                            animation: `fadeIn 0.3s ease-out forwards ${totalDelay}s`,
                            cursor: 'pointer',
                            color: cuspidesEfectivas.includes(cuspideId)
                              ? '#7ebcec'
                              : 'var(--list-text)',
                          }}
                          onClick={() => manejarClickCuspide(cuspideId)}
                        >
                          <span className="planet-symbol">
                            <AstroGlyph kind="sign" name={casa.signo} />
                          </span>
                          {i18n.language === 'es'
                            ? `Casa ${numeroCasa} a ${casa.grado}°${casa.minutos}' de ${casa.signo}`
                            : `House ${numeroCasa} at ${casa.grado}°${casa.minutos}' in ${casa.signo}`}
                        </li>
                      );
                    })}
                  </>
                )}
              </ul>
            )}
          </div>
          <div className="time-controls-container"></div>
        </div>
        <div className="square-item">
          <div className="top-space" style={{ justifyContent: 'start' }}></div>
          <div className="table-container" ref={container3Ref}>
            <div className="top-space"></div>
            <div className="aspects-table-container">
              {matrizActiva.length > 0 && cuspidesSeleccionadas.length === 0 && (
                <table
                  className={`aspects-table ${showTable ? '' : 'hidden'}`}
                  style={{
                    '--table-cell-padding-bottom': `calc(100% / ${listaActiva.length + 5})`,
                  }}
                >
                  <thead>
                    <tr
                      className={showTable ? 'fade-in-row-animation' : ''}
                      style={{ animationDelay: getRowAnimationDelay(0) }}
                    >
                      <th className="table-cell" style={{ cursor: 'default' }}>
                        <div className="table-cell-content" />
                      </th>
                      {listaActiva.map((planeta) => (
                        <th
                          key={planeta}
                          className={`table-cell table-header-cell ${seleccionEfectiva.includes(planeta) ? 'selected' : ''}`}
                          style={{
                            color:
                              seleccionEfectiva.includes(planeta)
                                ? '#7ebcec'
                                : resultadoConCasas
                                  ? 'var(--list-text)'
                                  : undefined,
                          }}
                          title={planeta}
                          onClick={() => manejarClickPlaneta(planeta)}
                        >
                          <div className="table-cell-content">
                            <AstroGlyph kind="planet" name={planeta} />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {listaActiva.map((planetaFila, indexFila) => (
                      <tr
                        key={planetaFila}
                        className={showTable ? 'fade-in-row-animation' : ''}
                        style={{ animationDelay: getRowAnimationDelay(indexFila + 1) }}
                      >
                        <th
                          className={`table-cell table-header-cell ${seleccionEfectiva.includes(planetaFila) ? 'selected' : ''}`}
                          title={planetaFila}
                          onClick={() => manejarClickPlaneta(planetaFila)}
                        >
                          <div className="table-cell-content">
                            <AstroGlyph kind="planet" name={planetaFila} />
                          </div>
                        </th>
                        {matrizFiltrada[indexFila].map((aspectoInfo, indexColumna) => (
                          <td
                            key={`${indexFila}-${indexColumna}`}
                            className="table-cell table-data-cell"
                            style={{
                              fontSize: 'clamp(0.75em, 1.25vw, 1.2em)',
                              color: aspectoInfo?.nombre
                                ? coloresAspectos[aspectoInfo.nombre]
                                : resultadoConCasas
                                  ? '#7ebcec'
                                  : '#b3b3b3',
                            }}
                            title={indexFila === indexColumna ? '' : aspectoInfo?.nombre}
                            onClick={() => {
                              if (aspectoInfo?.nombre) handleAspectoClick(aspectoInfo.nombre);
                              else if (aspectoFiltrado) {
                                limpiarHovers();
                                setAspectoFiltrado(null);
                              }
                            }}
                          >
                            <div className="table-cell-content">
                              {indexFila === indexColumna ? (
                                '.'
                              ) : aspectoInfo?.nombre ? (
                                <AstroGlyph kind="aspect" name={aspectoInfo.nombre} />
                              ) : null}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {matrizAspectosCasa.length > 0 && cuspidesSeleccionadas.length > 0 && (
                <table
                  className={`aspects-table ${showTable ? '' : 'hidden'}`}
                  style={{ '--table-cell-padding-bottom': `calc(100% / ${12 + 5})` }}
                >
                  <thead>
                    <tr
                      className={showTable ? 'fade-in-row-animation' : ''}
                      style={{ animationDelay: getRowAnimationDelay(0) }}
                    >
                      <th className="table-cell" style={{ cursor: 'default' }}>
                        <div className="table-cell-content" />
                      </th>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((casa) => {
                        const cuspideId = `home-house-${casa}`;
                        const isSelected = cuspidesSeleccionadas.includes(cuspideId);
                        return (
                          <th
                            key={cuspideId}
                            className={`table-cell table-header-cell ${isSelected ? 'selected' : ''}`}
                            style={{ color: isSelected ? '#7ebcec' : 'var(--list-text)' }}
                            title={`Casa ${casa}`}
                            onClick={() => manejarClickCuspide(cuspideId)}
                          >
                            <div
                              className="table-cell-content"
                              style={{ fontFamily: 'Effra', fontSize: 'clamp(0.5em, .9vw, 1em)' }}
                            >
                              {`C${casa}`}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {planetasListaConAngulos.map((planetaFila, indexFila) => {
                      const isSelectedRow = planetasSeleccionados.includes(planetaFila);
                      return (
                        <tr
                          key={planetaFila}
                          className={showTable ? 'fade-in-row-animation' : ''}
                          style={{ animationDelay: getRowAnimationDelay(indexFila + 1) }}
                        >
                          <th
                            className={`table-cell table-header-cell ${isSelectedRow ? 'selected' : ''}`}
                            title={planetaFila}
                            onClick={() => manejarClickPlaneta(planetaFila)}
                          >
                            <div className="table-cell-content">
                              <AstroGlyph kind="planet" name={planetaFila} />
                            </div>
                          </th>
                          {matrizAspectosCasa[indexFila]?.map((aspectoInfo, indexCasa) => {
                            const cuspideId = `home-house-${indexCasa + 1}`;
                            const isSelectedCol = cuspidesSeleccionadas.includes(cuspideId);
                            const aspectoAMostrar = isSelectedCol ? aspectoInfo : null;
                            return (
                              <td
                                key={`${indexFila}-${indexCasa}`}
                                className="table-cell table-data-cell"
                                style={{
                                  fontSize: 'clamp(0.75em, 1.25vw, 1.2em)',
                                  color: aspectoAMostrar?.nombre
                                    ? coloresAspectos[aspectoAMostrar.nombre]
                                    : '#7ebcec',
                                }}
                                title={aspectoAMostrar?.nombre}
                                onClick={() => {
                                  if (aspectoAMostrar?.nombre)
                                    handleAspectoClick(aspectoAMostrar.nombre);
                                  else if (aspectoFiltrado) {
                                    limpiarHovers();
                                    setAspectoFiltrado(null);
                                  }
                                }}
                              >
                                <div className="table-cell-content">
                                  {aspectoAMostrar?.nombre ? (
                                    <AstroGlyph kind="aspect" name={aspectoAMostrar.nombre} />
                                  ) : null}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="time-controls-container"></div>
          </div>
        </div>
      </div>
    </>
  );
}
export default Home;
